import { PoetryMetadata, PriorScan, Sku, Study } from '@/api';
import { ScanOption, StudyResult } from '@/shared/model';
import {
  formatMonthDayYear,
  formatTimestampToDate,
  hasStudyResultScanType,
  isEmpty,
} from '@/shared/util';

import { getServiceInfoNameList, SERVICE_INFO_ORDER } from '../component';
import { getSkuPackage } from './sku.util';

export function mapStudyToStudyResult(study: Study) {
  return {
    id: study.studyID,
    date: study.booking[0].utcStart,
    poetryMetadata: study.poetryMetadata,
    skuId: study.items.primary.skuID as Sku,
    timezone: study.location?.timezone,
  };
}

export function getScanName(skuId: Sku, poetryMetadata?: null | Partial<PoetryMetadata>): string {
  if (skuId === '900') {
    const imagingServices = (poetryMetadata?.servicesInfo ?? []).filter((service) =>
      SERVICE_INFO_ORDER.includes(service.code),
    );

    return getServiceInfoNameList(imagingServices).join(' • ');
  }

  return getSkuPackage(Number(skuId));
}

export function mapStudyResultsToPriorScans(
  previousStudyResults: StudyResult[],
  selectedScanIds: string[],
): PriorScan[] {
  return previousStudyResults
    .filter((study) => selectedScanIds.includes(study.id))
    .map((study) => ({
      scanDate: formatTimestampToDate(study.date, study.timezone),
      scanType: hasStudyResultScanType(study)
        ? study.scanType
        : getScanName(study.skuId, study.poetryMetadata),
      studyId: study.id,
    }));
}

export function mapStudyResultsToScanOptions(
  previousStudyResults: StudyResult[],
  selectedScanIds?: string[],
  dateFormat?: '2-digit' | 'long' | 'narrow' | 'numeric' | 'short',
): ScanOption[] {
  if (isEmpty(previousStudyResults)) {
    return [];
  }

  const resultsToMap = selectedScanIds
    ? previousStudyResults.filter((study) => selectedScanIds.includes(study.id))
    : previousStudyResults;

  return resultsToMap.map((study) => {
    const scanName = hasStudyResultScanType(study)
      ? study.scanType
      : getScanName(study.skuId, study.poetryMetadata);
    const formattedDate = dateFormat
      ? formatMonthDayYear(study.date, { monthFormat: dateFormat, timezone: study.timezone })
      : formatTimestampToDate(study.date, study.timezone);

    return {
      id: study.id,
      date: formattedDate,
      scanName,
    };
  });
}

export function extractPreviousStudyResults(studies: Study[], scanDate: number): StudyResult[] {
  if (isEmpty(studies)) {
    return [];
  }

  return studies
    .filter((study) => study.booking[0].utcStart < scanDate && study.status.published)
    .sort((a, b) => b.booking[0].utcStart - a.booking[0].utcStart)
    .map(mapStudyToStudyResult);
}

export function getMostRecentStudy(studies: Study[], scanDate: number) {
  return studies
    .filter((study) => study.booking[0].utcStart < scanDate && study.status.published)
    .sort(
      (a, b) =>
        new Date(b.booking[0].utcStart).getTime() - new Date(a.booking[0].utcStart).getTime(),
    )[0];
}
